import { auth } from 'botpress/shared'
import React, { useState } from 'react'
import { useLocation } from 'react-router-dom'

import api from '~/app/api'
import packageJson from '../../../../../package.json'
import { aesKeyStore, jwtKeyStore, subDataStore } from '../../../token-store'
import { secureSessionStorage, secureLocalStorage } from '../../utils/secureStorage'
import BlockedAccountScreen from './components/BlockedAccountScreen'
import DemoNotAllowedScreen from './components/DemoNotAllowedScreen'
import DemoOnlyScreen from './components/DemoOnlyScreen'
import MaintenanceScreen from './components/MaintenanceScreen'
import SubscriptionChangedScreen from './components/SubscriptionChangedScreen'
import VersionIncompatibleScreen from './components/VersionIncompatibleScreen'
import { LogoutProvider } from './contexts/LogoutContext'
import { useDevToolsProtection } from './hooks/useDevToolsProtection'
import { useSessionValidation } from './hooks/useSessionValidation'
import { useTabManager } from './hooks/useTabManager'
import { useWebSocketConnection } from './hooks/useWebSocketConnection'

const CURRENT_VERSION = packageJson.version


const MaintenanceWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation()
  const subData = subDataStore.get() || {}
  const workspaceRouteRegex = /(\/workspace|\/studio)(\/|$)/

  // State management
  const [isLoading, setIsLoading] = useState(false)

  const [isMaintenance, setIsMaintenance] = useState(() => {
    const savedMaintenanceState = secureSessionStorage.getItem('maintenance')
    return savedMaintenanceState ? JSON.parse(savedMaintenanceState).status : false
  })

  const [isBlocked, setIsBlocked] = useState(() => {
    const savedBlockedState = secureSessionStorage.getItem('accountBlocked')
    return savedBlockedState ? JSON.parse(savedBlockedState).isBlocked : false
  })

  const [isVersionIncompatible, setIsVersionIncompatible] = useState(() => {
    const savedVersionState = secureSessionStorage.getItem('versionIncompatible')
    return savedVersionState ? JSON.parse(savedVersionState).isIncompatible : false
  })

  const [versionInfo, setVersionInfo] = useState(() => {
    const savedVersionState = secureSessionStorage.getItem('versionIncompatible')
    if (savedVersionState) {
      const parsed = JSON.parse(savedVersionState)
      return parsed.versionInfo || { server: '', client: CURRENT_VERSION }
    }
    return { server: '', client: CURRENT_VERSION }
  })

  const [demoRestriction, setDemoRestriction] = useState<'demo-only' | 'demo-not-allowed' | null>(() => {
    const savedRestriction = secureSessionStorage.getItem('demoRestriction')
    return savedRestriction ? JSON.parse(savedRestriction).type : null
  })

  // Utility functions
  const handleLogout = () => {
    secureLocalStorage.clear()
    secureSessionStorage.clear()
    jwtKeyStore.delete()
    aesKeyStore.delete()
    auth.logout(() => api.getSecured())
  }

  const handleBlockStatus = (status: string) => {
    setIsBlocked(status === 'Blocked')
    secureSessionStorage.setItem('accountBlocked', JSON.stringify({
      isBlocked: status === 'Blocked',
    }))
  }

  const handleVersionUpdate = (serverVersion: string) => {
    const compareVersions = (version1: string, version2: string): number => {
      const v1Parts = version1.split('.').map(Number)
      const v2Parts = version2.split('.').map(Number)

      for (let i = 0; i < Math.max(v1Parts.length, v2Parts.length); i++) {
        const v1Part = v1Parts[i] || 0
        const v2Part = v2Parts[i] || 0

        if (v1Part > v2Part || v1Part < v2Part) {
          return -1
        }
      }
      return 0
    }

    const currentProjectVersion = CURRENT_VERSION
    const versionData = { server: serverVersion, client: currentProjectVersion }

    if (compareVersions(serverVersion, currentProjectVersion) < 0) {
      setVersionInfo(versionData)
      setIsVersionIncompatible(true)

      secureSessionStorage.setItem('versionIncompatible', JSON.stringify({
        isIncompatible: true,
        versionInfo: versionData,
        timestamp: Date.now()
      }))
    } else {
      secureSessionStorage.setItem('versionIncompatible', JSON.stringify({
        isIncompatible: false,
        versionInfo: versionData,
        timestamp: Date.now()
      }))
      setIsVersionIncompatible(false)
    }
  }

  const handleMaintenanceUpdate = (status: boolean) => {
    setIsMaintenance(status)
    secureSessionStorage.setItem('maintenance', JSON.stringify({ status }))
  }

  const handleDemoRestriction = (restrictionType: 'demo-only' | 'demo-not-allowed' | null) => {
    setDemoRestriction(restrictionType)
    if (restrictionType) {
      secureSessionStorage.setItem('demoRestriction', JSON.stringify({ type: restrictionType }))
    } else {
      secureSessionStorage.removeItem('demoRestriction')
    }
  }

  // devtool detection hook (runs only in production mode)
  useDevToolsProtection()

  // duplicate tab check hook
  const activeTab = useTabManager()

  // websocket connection hook
  useWebSocketConnection({
    currentVersion: CURRENT_VERSION,
    workspaceRouteRegex,
    pathname: location.pathname,
    activeTab,
    handlers: {
      onBlockStatus: handleBlockStatus,
      onVersionUpdate: handleVersionUpdate,
      onMaintenanceUpdate: handleMaintenanceUpdate,
      onDemoRestriction: handleDemoRestriction,
      onLogout: handleLogout,
      setIsLoading
    }
  })

  // session validation hook
  const { logoutSilently } = useSessionValidation({
    workspaceRouteRegex,
    pathname: location.pathname,
    activeTab,
    onLogout: handleLogout,
    isBootstrapping: isLoading
  })

  // Render loading state
  if (isLoading) {
    return (
      <div
        style={{
          height: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          fontSize: 20,
          color: '#444',
          fontWeight: 500,
        }}
      >
        Loading xMati...
      </div>
    )
  }

  // Render appropriate screen based on state
  if (isBlocked && workspaceRouteRegex.test(location.pathname)) {
    return <BlockedAccountScreen onLogout={handleLogout} />
  }

  if (demoRestriction === 'demo-only' && workspaceRouteRegex.test(location.pathname)) {
    return <DemoOnlyScreen />
  }

  if (demoRestriction === 'demo-not-allowed' && workspaceRouteRegex.test(location.pathname)) {
    return <DemoNotAllowedScreen />
  }

  if (isVersionIncompatible && workspaceRouteRegex.test(location.pathname)) {
    return (
      <VersionIncompatibleScreen
        serverVersion={versionInfo.server}
        clientVersion={versionInfo.client}
      />
    )
  }

  if (subData.subsChanged && workspaceRouteRegex.test(location.pathname)) {
    return <SubscriptionChangedScreen onLogout={handleLogout} />
  }

  if (isMaintenance && workspaceRouteRegex.test(location.pathname)) {
    return <MaintenanceScreen />
  }

  return <LogoutProvider logoutSilently={logoutSilently}>{children}</LogoutProvider>
}

export default MaintenanceWrapper
